import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  Text,
  Platform,
  Dimensions,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { clearToastNotification } from '../../../store/slices/actionPlanSlice';
import { clearToastNotification as clearStandUpToastNotification } from '../../../store/slices/standUpSlice';
import { useToast } from '../../../components/LhToastContainer';
import ActionPlanTable from '../components/ActionPlanTable';
import ActionPlanWelcome from '../components/ActionPlanWelcome';
import ActionPlanDashboardTitle from '../components/ActionPlanDashboardTitle';
import CalendarSection from '../components/CalendarSection';
import TableSection from '../components/TableSection';
import ActionPlanDetails from './ActionPlanDetails';
import { useActionPlanDashboard } from '../hooks/useActionPlanDashboard';
import { styles } from '../styles/dashboard.styles';
import type { ActionPlanDashboardProps, ActionPlanItem } from '../types';
import { useSelectedLocation } from '../../../hooks';
import { useTheme } from '../../../theme';

/**
 * ActionPlanDashboard
 *
 * Main dashboard component for managing action plans.
 * Displays action plans in three views: Hybrid (calendar + table), Table, and Calendar
 * Handles filtering, pagination, and status updates.
 */
const ActionPlanDashboard: React.FC<ActionPlanDashboardProps> = ({
  onBackPress,
  onCreateNewActionPlan,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  // Redux selectors and dispatch
  const dispatch = useDispatch<AppDispatch>();
  const selectedLocation = useSelectedLocation();
  const toastNotification = useSelector((state: RootState) => state.actionPlan?.toastNotification);
  const toast = useToast();

  // Clear StandUp toast notification on mount to prevent cross-module notifications
  useEffect(() => {
    dispatch(clearStandUpToastNotification());
  }, [dispatch]);

  // Display toast notification when it appears in Redux
  useEffect(() => {
    if (toastNotification) {
      console.log('Displaying toast from Redux:', toastNotification);
      toast.showToast({
        header: toastNotification.header,
        subtext: toastNotification.subtext,
        variant: toastNotification.variant,
        position: toastNotification.position || 'top',
        duration: toastNotification.duration || 4000,
      });
      
      // Clear the toast from Redux after displaying
      dispatch(clearToastNotification());
    }
  }, [toastNotification, toast, dispatch]);

  // Use custom hook to manage all dashboard logic
  const {
    activeTab,
    actionPlanData,
    isLoading,
    error,
    pagination,
    currentPage,
    filters,
    filterOptions,
    events,
    calendarEventsLoading,
    calendarEventsError,
    calendarDate,
    isFiltersExpanded,
    filterAnimation,
    showActionPlanDetails,
    selectedActionPlanId,
    selectedActionPlanData,
    isEditMode,
    isMobile,
    isTablet,
    handleTabChange,
    handleFilterChange,
    handleStatusChange,
    handleDeleteSuccess,
    handlePageChange,
    handlePageSizeChange,
    handleRetry,
    handleCalendarRetry,
    handleCalendarNavigationChange,
    toggleFiltersExpansion,
    handleEditActionPlan,
    handleViewActionPlanDetails,
    handleCalendarEventViewDetails,
    handleBackFromDetails,
    resetFilters,
    getResponsiveItemsPerRow,
    handleCalendarViewModeChange,
    handleDateSelect,
    handleEventPress,
    handleEventRemove,
    handleEventDetailsPress,
    handleShowAllEvents,
    setActionPlanData,
    setSelectedActionPlanData,
    setFilterDataset,
  } = useActionPlanDashboard(selectedLocation);

  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([handleRetry(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  };

  // Redirect to dashboard whenever location changes while ActionPlanDetails is open
  const prevLocationIdRef = useRef(selectedLocation?.id);
  const handleBackFromDetailsRef = useRef(handleBackFromDetails);
  useEffect(() => { handleBackFromDetailsRef.current = handleBackFromDetails; }, [handleBackFromDetails]);

  useEffect(() => {
    const prevId = prevLocationIdRef.current;
    prevLocationIdRef.current = selectedLocation?.id;
    if (prevId && selectedLocation?.id && prevId !== selectedLocation.id && showActionPlanDetails) {
      handleBackFromDetailsRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, showActionPlanDetails]);

  // Memoized calendar section to prevent unnecessary re-renders
  const calendarSection = useMemo(
    () => {
      // Force weekly mode on iOS mobile
      const getViewMode = () => {
        if (Platform.OS === 'ios') {
          // Check if it's mobile (not iPad)
          const { width } = require('react-native').Dimensions.get('window');
          if (width < 768) {
            return 'weekly'; // Always weekly on iOS mobile
          }
        }
        // Default behavior for web, iPad, Android
        return activeTab === 'Hybrid' ? 'weekly' : 'monthly';
      };
      
      return (
        <CalendarSection
          events={events}
          onDateSelect={handleDateSelect}
          onEventPress={handleEventPress}
          onEventRemove={handleEventRemove}
          onEventDetailsPress={handleEventDetailsPress}
          onShowAllEvents={handleShowAllEvents}
          onNavigationChange={handleCalendarNavigationChange}
          onViewModeChange={handleCalendarViewModeChange}
          onViewDetails={handleCalendarEventViewDetails}
          viewMode={getViewMode()}
          isLoading={calendarEventsLoading}
        />
      );
    },
    [
      events,
      activeTab,
      calendarEventsLoading,
      handleCalendarNavigationChange,
      handleCalendarViewModeChange,
      handleCalendarEventViewDetails,
      handleDateSelect,
      handleEventPress,
      handleEventRemove,
      handleEventDetailsPress,
      handleShowAllEvents,
    ]
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'Calendar':
        return (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0066CC" colors={["#0066CC"]} title="Refreshing..." />}>
            {calendarSection}
          </ScrollView>
        );
      case 'Table':
        return (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0066CC" colors={["#0066CC"]} title="Refreshing..."  />}>
            <TableSection
              actionPlanData={actionPlanData}
              isLoading={isLoading}
              error={error}
             // title="Action Plan"
              onEditPress={handleEditActionPlan}
              onViewDetailsPress={handleViewActionPlanDetails}
              onDeleteSuccess={handleDeleteSuccess}
              onStatusChange={handleStatusChange}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              currentFilters={filters}
              isMobile={isMobile}
              isTablet={isTablet}
              getResponsiveItemsPerRow={getResponsiveItemsPerRow}
              isFiltersExpanded={isFiltersExpanded}
              filterAnimation={filterAnimation}
              onToggleFilters={toggleFiltersExpansion}
              onClearAllFilters={resetFilters}
              // Pagination props
              currentPage={currentPage}
              totalPages={pagination?.totalPages || 1}
              pageSize={filters.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isHybridView={false}
            />
            {error && (
              <Text style={styles.retryButton} onPress={handleRetry}>
                Tap to retry
              </Text>
            )}
          </ScrollView>
        );
      case 'Hybrid':
      default:
        return (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0066CC" colors={["#0066CC"]} title="Refreshing..." />}>
            {calendarSection}
            <TableSection
              actionPlanData={actionPlanData}
              isLoading={isLoading}
              error={error}
              title="Action Plan"
              onEditPress={handleEditActionPlan}
              onViewDetailsPress={handleViewActionPlanDetails}
              onDeleteSuccess={handleDeleteSuccess}
              onStatusChange={handleStatusChange}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              currentFilters={filters}
              isMobile={isMobile}
              isTablet={isTablet}
              getResponsiveItemsPerRow={getResponsiveItemsPerRow}
              isFiltersExpanded={isFiltersExpanded}
              filterAnimation={filterAnimation}
              onToggleFilters={toggleFiltersExpansion}
              onClearAllFilters={resetFilters}
              // Pagination props
              currentPage={currentPage}
              totalPages={pagination?.totalPages || 1}
              pageSize={filters.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isHybridView={true}
            />
            {error && (
              <Text style={styles.retryButton} onPress={handleRetry}>
                Tap to retry
              </Text>
            )}
          </ScrollView>
        );
    }
  };

  // Render ActionPlanDetails if navigating to it
  if (showActionPlanDetails) {
    const handleActionPlanSaved = (updatedData: any) => {
      if (updatedData?.STATUS === 'deleted' && updatedData?.ID) {
        setActionPlanData((prevData: ActionPlanItem[]) => prevData.filter(item => item.ID !== Number(updatedData.ID)));
        setFilterDataset((prevData: ActionPlanItem[]) => prevData.filter(item => item.ID !== Number(updatedData.ID)));
        setSelectedActionPlanData(null);
        console.log('Action plan removed from dashboard list (deleted):', updatedData.ID);
        return;
      }

      // Update the action plan in the list
      setActionPlanData((prevData: ActionPlanItem[]) => {
        return prevData.map((item: ActionPlanItem) =>
          item.ID === updatedData.ID ? updatedData : item
        );
      });
      
      // Also update the filter dataset so filter dropdowns reflect the updated type/source/priority
      setFilterDataset((prevData: ActionPlanItem[]) => {
        return prevData.map((item: ActionPlanItem) =>
          item.ID === updatedData.ID ? updatedData : item
        );
      });
      
      // Also update the selected data so it stays in sync
      setSelectedActionPlanData(updatedData);
      
      console.log('Action plan updated in dashboard list:', updatedData.ID);
      console.log('Filter dataset also updated with new type/source/priority values');
    };

    return (
      <ActionPlanDetails
        onBackPress={handleBackFromDetails}
        actionPlanId={selectedActionPlanId || undefined}
        actionPlanData={selectedActionPlanData || undefined}
        initialMode={isEditMode ? 'edit' : 'view'}
        onActionPlanSaved={handleActionPlanSaved}
        onActionPlanDeleted={(deletedId: string) => {
          // Call the same handler as overflow delete (updates calendar events!)
          handleDeleteSuccess(deletedId);
          // Also handle UI state for ActionPlanDetails
          setSelectedActionPlanData(null);
          console.log('Action plan removed from dashboard list via onActionPlanDeleted:', deletedId);
        }}
      />
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, isDark && { backgroundColor: colors.background.page }]} edges={['bottom', 'left', 'right']}>
      <View style={[styles.container, isDark && { backgroundColor: colors.background.page }]} testID="action-plan-dashboard">
        <ActionPlanWelcome onCreateNewActionPlan={onCreateNewActionPlan} />
        <ActionPlanDashboardTitle selectedTab={activeTab} onTabChange={handleTabChange} />
        {renderContent()}
      </View>
    </SafeAreaView>
  );
};

export default ActionPlanDashboard;
